import { useState } from "react";
import { Icon } from "@iconify/react";
import { Button } from "@/shared/components/button";
import { cn } from "@/shared/lib/utils";
import type { EmpiIdentityComparison, EmpiIdentityDemographics, EmpiIdentityReason } from "@/shared/types/empi";
import type { ReviewAnswer } from "@/portals/patient/api/reviewItemsApi";
import { IDENTITY_REASON_TEXT, MATCH_BAND_TEXT, formatEmpiDate } from "@/features/empi/empiPresentation";

/**
 * IDENTITY_MISMATCH (Module 5): an uploaded record's details didn't clearly
 * match the patient's profile. The account's details and the record's sit
 * side by side; the patient says whether it's them. Patient-only — no
 * clinician ever sees or decides this. The answer choices come from the
 * item's allowedAnswers (the set the backend enforces), in a fixed order.
 */

/** Display order only — which of these appear is decided by allowedAnswers. */
const ORDER: ReviewAnswer[] = ["SAME_PERSON", "NOT_SAME_PERSON", "NOT_SURE"];

const CHOICES: Partial<Record<ReviewAnswer, { label: string; help: string }>> = {
  SAME_PERSON: { label: "Yes, this is me", help: "These records will be added to your health record." },
  NOT_SAME_PERSON: { label: "No, this isn't me", help: "These records will be hidden from your health record." },
  NOT_SURE: { label: "I'm not sure", help: "Nothing changes for now. The records stay on hold and you can answer later." },
};

export const IDENTITY_MESSAGES = {
  same: "Thanks, these records are now part of your health record.",
  /** An answer arriving after another one already confirmed this upload. */
  alreadyConfirmed: "This upload was already confirmed as yours by your earlier answer.",
  /** "Yes" also confirmed other uploads with exactly the same details. */
  alsoConfirmed: (count: number) =>
    `We also added ${count} other ${count === 1 ? "upload" : "uploads"} showing exactly the same details.`,
  notSame: "Done. These records are hidden from your health record.",
  notSure: "Okay, these records stay on hold until you're sure.",
  deleteTitle: "Delete this upload from MedSync?",
  deleteBody:
    "These records won't be added to your health record. If you delete this upload, it's removed from your account right away and permanently erased after 30 days for your security.",
  deleteConfirm: "Delete upload",
  deleteKeep: "Keep it hidden",
  deleted: "Upload deleted. It will be permanently erased in 30 days.",
};

/**
 * Nothing to compare: the upload carries no name or birth date (or no patient
 * details at all), so there is no side-by-side to show — just a plain message.
 * Same answers either way.
 */
export const NOTHING_TO_COMPARE_MESSAGES: Partial<Record<EmpiIdentityReason, string>> = {
  no_identifying_information:
    "We couldn't find enough identifying information in this upload to verify it automatically. Please confirm it belongs to you.",
  no_identity_evidence_at_all:
    "This upload doesn't include any patient details (like a name or date of birth) for us to check, so we can't verify it automatically. Please confirm it belongs to you.",
};

/**
 * A clear mismatch — the upload names someone else (a different first or last
 * name) — gets a sharper line than the generic "couldn't confirm" one.
 */
export const DIFFERENT_PERSON_MESSAGE =
  "This upload appears to name a different person than your profile. Please confirm this upload is yours, or that it wasn't uploaded by mistake.";

/**
 * The name "Yes" will keep as one of the patient's names (shown on their
 * profile, removable). Null when the record has no name and no birth date —
 * nothing is remembered then.
 */
export function rememberedDetails(uploaded: EmpiIdentityDemographics): string | null {
  const name = [uploaded.givenName, uploaded.middleName, uploaded.familyName].filter(Boolean).join(" ");

  if (!name && !uploaded.birthDate) return null;

  return [name && `“${name}”`, uploaded.birthDate && `born “${uploaded.birthDate}”`].filter(Boolean).join(", ");
}

export function namesDifferentPerson(reasons: EmpiIdentityReason[]): boolean {
  return reasons.includes("given_name_differs") || reasons.includes("family_name_differs");
}

/** "male" / "M" -> "m"; anything that isn't a stated male/female (prefer not to say, other, unknown) -> null. */
const sexOf = (value: string | null | undefined): "m" | "f" | null => {
  const v = (value ?? "").trim().toLowerCase();
  if (["m", "male", "man"].includes(v)) return "m";
  if (["f", "female", "woman"].includes(v)) return "f";
  return null;
};

const sexLabel = (value: string | null | undefined): string => {
  const v = (value ?? "").trim();
  if (!v) return "";
  const sex = sexOf(v);
  if (sex) return sex === "m" ? "Male" : "Female";
  const words = v.replace(/[_-]+/g, " ").toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const ROWS: {
  label: string;
  get: (d: EmpiIdentityDemographics) => string;
  /** When given: whether the two sides can be compared at all (else never highlighted). */
  comparable?: (a: EmpiIdentityDemographics, b: EmpiIdentityDemographics) => boolean;
}[] = [
  { label: "Name", get: (d) => [d.givenName, d.middleName, d.familyName].filter(Boolean).join(" ") },
  { label: "Date of birth", get: (d) => (d.birthDate ? formatEmpiDate(d.birthDate) : "") },
  // "Prefer not to say" (or other) isn't a sex to compare with a record's "Male": shown, never flagged.
  { label: "Sex", get: (d) => sexLabel(d.gender), comparable: (a, b) => sexOf(a.gender) !== null && sexOf(b.gender) !== null },
  { label: "Phone", get: (d) => d.phone ?? "" },
  { label: "Email", get: (d) => d.email ?? "" },
  { label: "Address", get: (d) => d.address ?? "" },
];

const same = (a: string, b: string) => a.trim().toLowerCase().replace(/\s+/g, " ") === b.trim().toLowerCase().replace(/\s+/g, " ");

const BRAND_BUTTON =
  "!h-10 !rounded-lg !border-transparent !bg-[#E4002B]/50 !px-6 !text-sm !font-semibold !text-white hover:!border-transparent hover:!bg-[#E4002B]/60 hover:!text-white disabled:!cursor-not-allowed disabled:!opacity-60";

export function IdentityReview({
  identity,
  allowedAnswers,
  sending,
  onSubmit,
  onNotMe,
  notMeOpen = false,
}: {
  identity: EmpiIdentityComparison;
  allowedAnswers: ReviewAnswer[];
  sending: boolean;
  onSubmit: (answer: ReviewAnswer) => void;
  /** "No, this isn't me" opens the delete popup at once (no Send); the popup sends the answer. */
  onNotMe?: () => void;
  /** The popup is open: that choice shows as picked meanwhile. */
  notMeOpen?: boolean;
}) {
  const [answer, setAnswer] = useState<ReviewAnswer | null>(null);
  const choices = ORDER.filter((a) => allowedAnswers.includes(a) && CHOICES[a]);
  const reasons = identity.reasons.filter((r) =>
    r !== "patient_confirmed_same_person" && r !== "patient_confirmed_not_same_person"
    && r !== "patient_confirmed_same_details" && r !== "matches_a_name_you_confirmed");
  const nothingToCompare = reasons.map((r) => NOTHING_TO_COMPARE_MESSAGES[r]).find(Boolean);
  const remembered = nothingToCompare ? null : rememberedDetails(identity.uploaded);

  const send = () => {
    if (answer) onSubmit(answer);
  };

  const pick = (choice: ReviewAnswer, selected: boolean) => {
    if (choice === "NOT_SAME_PERSON" && onNotMe) {
      setAnswer(null);
      onNotMe();
      return;
    }
    setAnswer(selected ? null : choice);
  };

  return (
    <div className="grid gap-6">
      {nothingToCompare ? (
        <section className="rounded-xl border border-[#F8C9D2] bg-[#FFF5F7] p-4" aria-label="Why we're asking" data-testid="identity-nothing-to-compare">
          {identity.uploadLabel && <p className="text-sm font-semibold text-[#0A2947]">From “{identity.uploadLabel}”</p>}
          <p className={cn("text-sm text-[#4B5563]", identity.uploadLabel && "mt-1")}>{nothingToCompare}</p>
        </section>
      ) : (
      <>
      <section className="rounded-xl border border-[#F8C9D2] bg-[#FFF5F7] p-5" aria-label="Why we're asking">
        {/* Heading: what happened, and which upload. */}
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#E4002B]/50">
            <Icon icon="lucide:user-x" className="h-4 w-4 text-white" />
          </div>
          <div className="min-w-0">
            <p className="!mb-0 text-[15px] font-bold leading-snug text-[#0A2947]">This record may not be yours</p>
            {identity.uploadLabel && (
              <p className="!mb-0 mt-1 flex min-w-0 items-center gap-1.5 text-xs text-[#6B7280]">
                <Icon icon="lucide:file-text" className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">From {identity.uploadLabel}</span>
              </p>
            )}
          </div>
        </div>

        {namesDifferentPerson(reasons) ? (
          <p className="!mb-0 !mt-6 text-sm leading-6 text-[#374151]" data-testid="identity-different-person">{DIFFERENT_PERSON_MESSAGE}</p>
        ) : (
          identity.matchBand && <p className="!mb-0 !mt-6 text-sm leading-6 text-[#374151]">{MATCH_BAND_TEXT[identity.matchBand]}</p>
        )}

        {/* What we noticed, one per line. */}
        {reasons.length > 0 && (
          <ul className="!mb-0 !mt-4 grid gap-2.5 !pl-0" data-testid="identity-reasons">
            {reasons.map((reason) => (
              <li key={reason} className="flex items-start gap-2.5 text-sm leading-5 text-[#4B5563]">
                <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#E4002B]/50" aria-hidden="true" />
                {IDENTITY_REASON_TEXT[reason]}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-label="Your details compared with the record">
        <div className="overflow-hidden rounded-xl border border-[#E5E7EB]">
          <table className="w-full table-fixed text-left text-sm">
            <thead className="bg-[#F9FAFB] text-xs text-[#6B7280]">
              <tr>
                <th className="w-1/4 px-4 py-2 font-semibold" scope="col"><span className="sr-only">Detail</span></th>
                <th className="px-4 py-2 font-semibold" scope="col">Your profile</th>
                <th className="px-4 py-2 font-semibold" scope="col">This record</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row) => {
                const mine = row.get(identity.account);
                const theirs = row.get(identity.uploaded);
                const comparable = row.comparable ? row.comparable(identity.account, identity.uploaded) : true;
                const differs = Boolean(comparable && mine && theirs && !same(mine, theirs));
                return (
                  <tr key={row.label} data-testid="identity-row" data-differs={differs || undefined} className={cn("border-t border-[#E5E7EB]", differs && "bg-[#FEF6DC]")}>
                    <th scope="row" className={cn("px-4 py-2.5 text-xs font-semibold", differs ? "text-[#946200]" : "text-[#6B7280]")}>{row.label}</th>
                    <td className="break-words px-4 py-2.5 font-bold text-[#111827]">{mine || <span className="font-normal text-[#9CA3AF]">Not given</span>}</td>
                    <td className="break-words px-4 py-2.5 font-bold text-[#111827]">{theirs || <span className="font-normal text-[#9CA3AF]">Not given</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* One question per file: the other ways it printed the name. */}
        {(identity.otherReads?.length ?? 0) > 0 && (
          <p className="!mb-0 mt-3 text-xs text-[#6B7280]" data-testid="identity-other-reads">
            This file also prints the name as: {identity.otherReads!.map((name) => `“${name}”`).join(", ")}
          </p>
        )}
      </section>
      </>
      )}

      <section>
        <p id="identity-question" className="!mb-5 text-sm font-extrabold text-[#0A2947]">Is this record about you?</p>
        <div role="radiogroup" aria-labelledby="identity-question" className="grid gap-3 sm:grid-cols-3">
          {choices.map((choice) => {
            const selected = answer === choice || (choice === "NOT_SAME_PERSON" && notMeOpen);
            return (
              <Button
                key={choice}
                variant="outline"
                role="radio"
                aria-checked={selected}
                onClick={() => pick(choice, selected)}
                className={cn(
                  "!flex !h-auto !w-full !flex-col !items-start !gap-1 !whitespace-normal !rounded-2xl !px-4 !py-3 !text-left !text-[#0A2947]",
                  selected
                    ? "!border-2 !border-[#F17893] !bg-[#FFF2F4] hover:!border-[#F17893] hover:!bg-[#FFF2F4]"
                    : "!border-[#DDE1E8] !bg-white hover:!border-[#F17893]/50 hover:!bg-white",
                )}
              >
                <span className="flex items-center gap-2 text-sm font-semibold">
                  <Icon icon={selected ? "lucide:circle-dot" : "lucide:circle"} className={cn("h-4 w-4 shrink-0", selected ? "text-[#E4002B]" : "text-[#9CA3AF]")} />
                  {CHOICES[choice]!.label}
                </span>
                <span className="text-xs font-normal text-[#6B7280]">{CHOICES[choice]!.help}</span>
              </Button>
            );
          })}
        </div>
        {/* Before they send: what "Yes" will also do from now on. */}
        {answer === "SAME_PERSON" && remembered && (
          <div data-testid="identity-remember-note" className="mt-4 flex items-start gap-2.5 rounded-xl border border-[#DDE1E8] bg-[#F7F9FC] p-4 text-sm leading-6 text-[#374151]">
            <Icon icon="lucide:info" className="mt-1 h-4 w-4 shrink-0 text-[#0A2947]" />
            <p className="!mb-0">
              We'll remember this answer ({remembered}). The name is saved under “Other names” on your profile,
              where you can remove it. Any other record with one of your names and your date of birth will be added
              to your health record without asking you again.
            </p>
          </div>
        )}
      </section>

      <div className="flex justify-end">
        <Button disabled={!answer} loading={sending} onClick={send} className={BRAND_BUTTON}>
          Send
        </Button>
      </div>
    </div>
  );
}
